import { Injectable } from '@angular/core';
import { ApplicationInsights, IConfig, IConfiguration } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { TcsConfiguration } from 'src/app/app.config';
import { TcsConfigurationService } from 'src/app/core/configuration.service';
import { environment } from 'src/environments/environment';
import { patchTelemetryProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { uuid } from 'src/util/uuid';
import { BehaviorSubject, filter, Observable } from 'rxjs';

const tabId = uuid();
export interface ApplicationInsightsConfiguration {
  enabled: boolean;
  instrumentationKey: string | null;
}

function bootstrapApplicationInsights({
  applicationInsights: { enabled, instrumentationKey },
  tcs: { graphqlUri },
}: TcsConfiguration) {
  if (enabled) {
    const appInsightsPlugin = new AngularPlugin();

    if (instrumentationKey == null) {
      // eslint-disable-next-line no-console
      console.warn('Application Insights is enabled but instrumentation key was not provided.');
    } else if (appInsightsPlugin == null) {
      // eslint-disable-next-line no-console
      console.warn('Application insights was enabled but SDK was not loaded properly.');
    } else {
      const config: IConfiguration & IConfig = {
        instrumentationKey,
        // We set correlation headers manually - App Insights assumes non-spa environment
        disableCorrelationHeaders: true,
        disableExceptionTracking: true,
        autoTrackPageVisitTime: false,
        enableDebug: !environment.production,
        maxAjaxCallsPerView: -1,
        // disabling cookie use locally to avoid this exception: https://github.com/Microsoft/ApplicationInsights-JS/issues/482
        disableCookiesUsage: !environment.production,
        loggingLevelConsole: !environment.production ? 1 : 2,
      };

      const applicationInsights = new ApplicationInsights({ config, queue: [] });
      applicationInsights.loadAppInsights();
      return applicationInsights;
    }
  }
}

function initializeTelemetry(applicationInsights: ApplicationInsights, graphqlUri: string) {
  applicationInsights.appInsights.queue = [];

  // Add tab ID
  applicationInsights.addTelemetryInitializer((env) => {
    if (env?.baseData?.properties != null) {
      env.baseData.properties.tabId = tabId;
    }
  });
  applicationInsights.addTelemetryInitializer((env) => {
    if (env?.baseData?.properties != null) {
      env.baseData.properties.tabId = tabId;
    }
  });

  // Don't track network exceptions
  applicationInsights.addTelemetryInitializer((env) => {
    if (env?.baseType === 'ExceptionData') {
      const isNetworkException =
        env?.baseData?.exceptions.find(
          (e: { typeName?: string; message?: string }) =>
            e.typeName === 'Error' && e.message != null && e.message.includes('Network error'),
        ) != null;
      if (isNetworkException) {
        /*
          We can't add very much helpful information to network exceptions, so we don't send
          exception telemetry to application insights to reduce noise. Failed network requests
          are observed in application insights via dependency tracking, where we have the ability
          to add more helpful information about the request and errors.
           */
        return false;
      }
    }
  });

  // Don't automatically track GraphQL dependencies (we do that manually)
  applicationInsights.addTelemetryInitializer((env) => {
    if (env?.baseType === 'RemoteDependencyData' && env?.baseData?.target != null) {
      const target = env.baseData.target;
      const graphQLUri = graphqlUri;
      const isGraphQLRequest = graphQLUri.includes(target);
      const properties: { [name: string]: string | undefined } | undefined =
        env.baseData.properties;
      if (isGraphQLRequest) {
        const trackGraphQLRequest = properties != null && properties.__trackMe === 'true';
        if (!trackGraphQLRequest) {
          // We ignore the default dependency tracking, instead only honoring calls to
          // track dependencies that we've explicitly tracked via
          // app-insights-dependeny-tracking.apollo-link.ts
          return false;
        }

        delete env.baseData.properties.__trackMe;
        env.baseData.type = 'graphql';
        let operationNameSet = false;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { gqlOperationType, gqlOperationName, variables } = properties!;
        env.baseData.data = variables;
        if (gqlOperationType != null && gqlOperationName != null) {
          const operationName = gqlOperationType + ' ' + gqlOperationName;
          if (env.tags != null) {
            env.tags['ai.operation.name'] = operationName;
          }
          env.baseData.name = operationName;
          operationNameSet = true;
        }
        const operationId = env.baseData.id;
        if (env.tags != null) {
          env.tags['ai.operation.id'] = operationId;
        }
        if (!operationNameSet) {
          let operationName: string | undefined = env.baseData.data;
          if (typeof operationId === 'string') {
            operationName = operationId.split('-')[0];
          }
          if (env.tags != null) {
            env.tags['ai.operation.name'] = operationName;
          }
          env.baseData.name = operationName;
        }
      } else {
        let operationName = 'website-unknown';
        if (target.includes('pndsn.com') === true) {
          env.baseData.type = 'PubNub';
          // pubnub
          if (target.includes('heartbeat') === true) {
            operationName = 'pubnub-heartbeat';
          } else {
            operationName = 'pubnub';
          }
          const resultCode = Number(env.baseData.responseCode);
          if (resultCode === 0) {
            // Don't log PubNub dependencies with a result code of 0.
            // PubNub cancels requests as part of the unsubscribe process, resulting in 0 resultCode
            // Logging these events as failed depenencies just adds noise to the logs
            return false;
          }
        }

        const opId = operationName + '-' + uuid();
        if (env.tags != null) {
          env.tags['ai.operation.id'] = opId;
          env.tags['ai.operation.name'] = operationName;
        }
      }
    }
  });

  applicationInsights.addTelemetryInitializer((env) => {
    if (
      env?.baseType === 'ExceptionData' &&
      env.baseData?.exceptions[0]?.message.includes('command failed:') === true
    ) {
      // This will be tracked via dependencies. Suppressing exceptions to reduce noise.
      return false;
    }
  });

  // Set cloud_roleName
  applicationInsights.addTelemetryInitializer((env) => {
    if (env.tags != null) {
      env.tags['ai.device.roleName'] = 'website';
    }
  });

  // Always include current href
  applicationInsights.addTelemetryInitializer((env) => {
    patchTelemetryProps(env, { href: window.location.href });
  });

  applicationInsights.addTelemetryInitializer((env) => {
    const operationId = env?.baseData?.operationId;
    if (operationId != null && env.tags != null) {
      env.tags['ai.operation.id'] = operationId;
    }
  });

  applicationInsights.addTelemetryInitializer((env) => {
    const operationId: string | null = env?.baseData?.properties?.operationId;
    if (operationId != null) {
      delete env?.baseData?.properties.operationId;
      if (env.tags != null) {
        env.tags['ai.operation.id'] = operationId;
      }
    }
  });
}
@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  public appInsights: ApplicationInsights | undefined;
  private _initialized$ = new BehaviorSubject(false);
  constructor(private _config: TcsConfigurationService) {}

  get intialized$(): Observable<boolean> {
    return this._initialized$.asObservable().pipe(filter((init) => init));
  }

  public init() {
    if (this._initialized$.getValue()) {
      return;
    }
    this.appInsights = bootstrapApplicationInsights(this._config.value);
    if (this.appInsights != null) {
      initializeTelemetry(this.appInsights, this._config.value.tcs.graphqlUri);
    }
    this._initialized$.next(true);
  }
}
